import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { PricingCard } from './PricingCard';
import { useRevenueCat } from '@/hooks/useRevenueCat';
import { SubscriptionTier } from '@/lib/entitlements';
import { toast } from 'sonner';

interface UpgradeModalProps {
    isOpen: boolean;
    onClose: () => void;
    feature?: string;
    requiredTier?: SubscriptionTier;
}

export function UpgradeModal({ isOpen, onClose, feature, requiredTier = 'plus' }: UpgradeModalProps) {
    const [isAnnual, setIsAnnual] = useState(true);
    const { purchase, isLoading } = useRevenueCat();

    const handlePurchase = async (pkg: string) => {
        try {
            await purchase(pkg);
            toast.success('Upgrade successful! Enjoy your new features.');
            onClose();
        } catch (error) {
            toast.error('Purchase failed. Please try again.');
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl">
                <DialogHeader className="text-center pb-6">
                    <DialogTitle className="text-3xl font-bold">
                        {feature ? `Unlock ${feature}` : 'Upgrade Your Plan'}
                    </DialogTitle>
                    <DialogDescription className="text-lg">
                        Choose the plan that fits your needs
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center justify-center gap-4 mb-8">
                    <Label htmlFor="billing-toggle" className={!isAnnual ? "font-bold" : ""}>Monthly</Label>
                    <Switch
                        id="billing-toggle"
                        checked={isAnnual}
                        onCheckedChange={setIsAnnual}
                    />
                    <Label htmlFor="billing-toggle" className={isAnnual ? "font-bold" : ""}>
                        Annual <span className="text-green-600 text-xs ml-1 font-normal">(Save 37%)</span>
                    </Label>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <PricingCard
                        name="PLUS"
                        price={isAnnual ? "$29.99" : "$3.99"}
                        period={isAnnual ? "year" : "month"}
                        description="For active subscription managers"
                        features={[
                            "Unlimited subscriptions",
                            "CSV Export",
                            "Custom Categories",
                            "Multi-currency support",
                            "Priority Support"
                        ]}
                        buttonText={requiredTier === 'plus' ? "Get PLUS" : "Upgrade to PLUS"}
                        onSelect={() => handlePurchase('plus_package')}
                        isLoading={isLoading}
                    />

                    <PricingCard
                        name="PRO"
                        price={isAnnual ? "$59.99" : "$7.99"}
                        period={isAnnual ? "year" : "month"}
                        description="For power users & families"
                        features={[
                            "Everything in PLUS",
                            "Gmail Integration",
                            "PDF Export",
                            "AI Smart Suggestions",
                            "Family Sharing"
                        ]}
                        isPopular
                        buttonText={requiredTier === 'pro' ? "Get PRO" : "Upgrade to PRO"}
                        onSelect={() => handlePurchase('pro_package')}
                        isLoading={isLoading}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}
